/* The field: Radar's object. The futures price over time on obsidian, confluence zones as bands of
   light (brighter = higher score), the expected move to the close right of now, and the sweep.
   Geometry and canvas drawing only; every word and number is DOM, placed from the same geometry. */
import type { Pt, Side, View, Zone } from './fieldModel';
import { priceTicks, timeTicks } from './fieldModel';
import { band, data } from '../tokens/semantic';

export interface Geom {
  W: number; H: number;
  /** plot box: left, top, right, bottom */
  L: number; T: number; xR: number; yB: number;
  plotW: number; plotH: number;
  /** right gutter (price axis, zone names, the price pill) */
  RT: number;
  narrow: boolean;
  v: View;
  X(t: number): number;
  Y(p: number): number;
  tAt(x: number): number;
  pAt(y: number): number;
}

export function geom(W: number, H: number, v: View): Geom {
  const narrow = W < 700;
  const L = 16, T = 18, B = 30, RT = narrow ? 108 : 164;
  const plotW = Math.max(40, W - L - RT), plotH = Math.max(40, H - T - B);
  const [lo, hi] = v.p, [t0, t1] = v.t;
  return {
    W, H, L, T, xR: L + plotW, yB: T + plotH, plotW, plotH, RT, narrow, v,
    X: t => L + ((t - t0) / (t1 - t0 || 1)) * plotW,
    Y: p => T + ((hi - p) / (hi - lo || 1)) * plotH,
    tAt: x => t0 + ((x - L) / plotW) * (t1 - t0),
    pAt: y => hi - ((y - T) / plotH) * (hi - lo),
  };
}

/** which part of the field a point is on: the plot, the price axis (right) or the time axis (bottom) */
export function areaAt(g: Geom, x: number, y: number): 'plot' | 'price' | 'time' {
  return x > g.xR ? 'price' : y > g.yB ? 'time' : 'plot';
}

export interface ZoneDraw {
  z: Zone;
  side: Side;
  /** score / strongest score, 0..1 */
  k: number;
  /** the zone in play (lit by the light layer too) */
  lit: boolean;
}

export interface Scene {
  runs: Pt[][];
  last: Pt | null;
  spot: number;
  em: number | null;
  now: number;
  sessions: [number, number][];
  zones: ZoneDraw[];
  f: number;
}

const RGB: Record<Side, string> = { above: hexRgb(data.up), below: hexRgb(data.down), inside: '242,242,240' };
const BAND = hexRgb(band.radar);

function hexRgb(h: string): string {
  const n = parseInt(h.slice(1), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

/** the sweep's position, eased (slow at both ends) */
export const sweepEase = (p: number): number => 0.5 - Math.cos(Math.PI * Math.max(0, Math.min(1, p))) / 2;

/** The sweep in flight: progress 0..1, and how lit the zones ahead of the line are
    (dark on entry, fully lit on a refresh, where the line only makes them flare). */
export interface SweepState { p: number; ahead: number }

/** Draws the field; `sweep` null at rest. */
export function drawField(ctx: CanvasRenderingContext2D, g: Geom, s: Scene, sweep: SweepState | null): void {
  const { L, T, xR, yB, plotW, plotH } = g;
  ctx.clearRect(0, 0, g.W, g.H);

  ctx.save();
  ctx.beginPath(); ctx.rect(L, T, plotW, plotH); ctx.clip();

  // the regular sessions, barely lifted from the ground
  ctx.fillStyle = 'rgba(255,255,255,0.016)';
  for (const [a, b] of s.sessions) {
    const x0 = Math.max(L, g.X(a)), x1 = Math.min(xR, g.X(b));
    if (x1 > x0) ctx.fillRect(x0, T, x1 - x0, plotH);
  }

  // hairline grid
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  for (const tk of priceTicks(g.v.p[0], g.v.p[1], s.f, plotH)) ctx.fillRect(L, Math.round(g.Y(tk.v)) - 0.5, plotW, 1);
  for (const tk of timeTicks(g.v.t[0], g.v.t[1], plotW)) ctx.fillRect(Math.round(g.X(tk.t)) - 0.5, T, 1, plotH);

  // the expected move to the close, right of now
  if (s.em) {
    const x0 = Math.max(L, g.X(s.now)), y0 = g.Y(s.spot + s.em), y1 = g.Y(s.spot - s.em);
    if (xR > x0) {
      ctx.fillStyle = 'rgba(255,255,255,0.045)';
      ctx.fillRect(x0, y0, xR - x0, y1 - y0);
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(x0, Math.round(y0) - 0.5, xR - x0, 1);
      ctx.fillRect(x0, Math.round(y1) - 0.5, xR - x0, 1);
    }
  }

  // zones, weakest first so the strongest light sits on top
  const sx = sweep === null ? null : L + sweepEase(sweep.p) * plotW;
  const order = [...s.zones].sort((a, b) => a.k - b.k);
  if (sx === null) order.forEach(d => zoneBand(ctx, g, d, 1, L, xR));
  else {
    order.forEach(d => zoneBand(ctx, g, d, 1, L, sx));
    order.forEach(d => zoneBand(ctx, g, d, sweep!.ahead, sx, xR));
    // the zones flare as the line passes
    order.forEach(d => zoneFlare(ctx, g, d, sx));
  }
  ctx.restore();

  // a short tick in the gutter carries each zone to its name
  for (const d of s.zones) {
    const y = g.Y(d.z.mid);
    if (y < T || y > yB) continue;
    ctx.fillStyle = `rgba(${RGB[d.side]},${(sweep === null || sweep.p >= 1 ? 1 : Math.max(0.4, sweep.ahead)) * (0.35 + 0.5 * d.k)})`;
    ctx.fillRect(xR, Math.round(y) - 0.5, 6, 1);
  }

  ctx.save();
  ctx.beginPath(); ctx.rect(L, T, plotW, plotH); ctx.clip();
  // the price: one thin line of ink, broken where nothing was recorded
  ctx.strokeStyle = 'rgba(242,242,240,0.92)';
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  for (const run of s.runs) {
    if (run.length < 2) {
      const q = run[0];
      if (q) { ctx.fillStyle = 'rgba(242,242,240,0.92)'; ctx.fillRect(g.X(q.t) - 1, g.Y(q.p) - 1, 2, 2); }
      continue;
    }
    ctx.beginPath();
    run.forEach((q, i) => (i ? ctx.lineTo(g.X(q.t), g.Y(q.p)) : ctx.moveTo(g.X(q.t), g.Y(q.p))));
    ctx.stroke();
  }
  // now: a dotted line from the last snapshot to the axis, and a dot on the price
  const yn = g.Y(s.spot), xl = s.last ? g.X(s.last.t) : g.X(s.now);
  ctx.strokeStyle = 'rgba(242,242,240,0.5)';
  ctx.lineWidth = 1;
  ctx.setLineDash([1, 3]);
  ctx.beginPath(); ctx.moveTo(Math.max(L, xl), Math.round(yn) - 0.5); ctx.lineTo(xR, Math.round(yn) - 0.5); ctx.stroke();
  ctx.setLineDash([]);
  if (s.last) {
    ctx.beginPath(); ctx.arc(g.X(s.last.t), g.Y(s.last.p), 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#F2F2F0'; ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = '#08080A'; ctx.stroke();
  }

  // the sweep: one line of Radar light with a short wake
  if (sx !== null && sweep !== null && sweep.p < 1) sweepLine(ctx, sx, T, plotH, Math.min(160, plotW * 0.18));
  ctx.restore();

  // the axis rule
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fillRect(L, yB - 0.5, plotW, 1);
}

/** One zone as a band of light: a soft vertical falloff around its span, a crisp line at its price
    (the score-weighted centre the backend names the zone by). */
function zoneBand(ctx: CanvasRenderingContext2D, g: Geom, d: ZoneDraw, dim: number, x0: number, x1: number): void {
  levelBand(ctx, { y1: g.Y(d.z.hi), y2: g.Y(d.z.lo), ym: g.Y(d.z.mid), k: d.k, side: d.side, lit: d.lit }, dim, x0, x1, g.T, g.yB);
}

/** A level or a span as Radar's band of light, in plain pixels (the landing and the extension draw with it).
    y1..y2 the span (equal for a single level), ym the crisp line, k its significance 0..1. */
export interface BandSpec { y1: number; y2: number; ym: number; k: number; side: Side; lit: boolean }

export function levelBand(ctx: CanvasRenderingContext2D, b: BandSpec, dim: number, x0: number, x1: number, top = -Infinity, bottom = Infinity): void {
  if (x1 <= x0) return;
  const { y1, y2 } = b, yc = (y1 + y2) / 2, core = Math.max(4, y2 - y1);
  const halo = 8 + 10 * b.k, t = yc - core / 2 - halo, bot = yc + core / 2 + halo;
  if (bot < top || t > bottom) return;
  // white light (price inside the zone) reads much brighter than green or red at the same alpha
  const a = (0.02 + 0.08 * b.k) * dim * (b.side === 'inside' ? 0.55 : 1), c = RGB[b.side];
  const gr = ctx.createLinearGradient(0, t, 0, bot);
  const e = halo / (bot - t);
  gr.addColorStop(0, `rgba(${c},0)`);
  gr.addColorStop(e, `rgba(${c},${a * 0.7})`);
  gr.addColorStop(0.5, `rgba(${c},${a})`);
  gr.addColorStop(1 - e, `rgba(${c},${a * 0.7})`);
  gr.addColorStop(1, `rgba(${c},0)`);
  ctx.fillStyle = gr;
  ctx.fillRect(x0, t, x1 - x0, bot - t);
  // the span's edges, when the zone is wider than a line
  if (y2 - y1 >= 3) {
    ctx.fillStyle = `rgba(${c},${(0.08 + 0.14 * b.k) * dim})`;
    ctx.fillRect(x0, Math.round(y1) - 0.5, x1 - x0, 1);
    ctx.fillRect(x0, Math.round(y2) - 0.5, x1 - x0, 1);
  }
  const la = (b.lit ? 0.95 : 0.25 + 0.55 * b.k) * dim;
  ctx.fillStyle = `rgba(${c},${la})`;
  const lw = b.lit || b.k > 0.6 ? 1.5 : 1;
  ctx.fillRect(x0, b.ym - lw / 2, x1 - x0, lw);
}

function zoneFlare(ctx: CanvasRenderingContext2D, g: Geom, d: ZoneDraw, sx: number): void {
  const ym = g.Y(d.z.mid);
  if (ym < g.T - 20 || ym > g.yB + 20) return;
  levelFlare(ctx, ym, d.k, d.side, sx);
}

/** the flare a passing sweep leaves on a level: a short gradient trailing the line */
export function levelFlare(ctx: CanvasRenderingContext2D, ym: number, k: number, side: Side, sx: number, w = 90): void {
  const c = RGB[side], a = 0.18 + 0.4 * k;
  const gr = ctx.createLinearGradient(sx - w, 0, sx, 0);
  gr.addColorStop(0, `rgba(${c},0)`);
  gr.addColorStop(1, `rgba(${c},${a})`);
  ctx.fillStyle = gr;
  ctx.fillRect(sx - w, ym - 1.5, w, 3);
}

/** the sweep line itself: Radar light with its wake */
export function sweepLine(ctx: CanvasRenderingContext2D, sx: number, top: number, height: number, wake = 160): void {
  const gr = ctx.createLinearGradient(sx - wake, 0, sx, 0);
  gr.addColorStop(0, `rgba(${BAND},0)`);
  gr.addColorStop(1, `rgba(${BAND},0.10)`);
  ctx.fillStyle = gr;
  ctx.fillRect(sx - wake, top, wake, height);
  ctx.fillStyle = `rgba(${BAND},0.9)`;
  ctx.fillRect(Math.round(sx) - 0.5, top, 1, height);
}

/** The dot on the price at the snapshot under the crosshair. */
export function drawPoint(ctx: CanvasRenderingContext2D, g: Geom, q: Pt | null): void {
  if (!q) return;
  const x = g.X(q.t), y = g.Y(q.p);
  if (x < g.L || x > g.xR || y < g.T || y > g.yB) return;
  ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fillStyle = '#F2F2F0'; ctx.fill();
}

export interface Tag { rank: number; y: number; side: Side; also: number[]; text: string }

/** Zone names in the gutter, strongest first; a name that would collide joins its neighbour ("Z1 · Z4"),
    and none sits on the price pill. */
export function zoneTags(zs: ZoneDraw[], g: Geom, pillY: number | null, fmt: (z: Zone) => string): Tag[] {
  const placed: Tag[] = [];
  const gap = 26;
  for (const d of [...zs].sort((a, b) => b.z.score - a.z.score || a.z.rank - b.z.rank)) {
    let y = g.Y(d.z.mid);
    if (y < g.T - 2 || y > g.yB + 2) continue;
    // a name is two lines (about 26 px), the pill 22 px: keep them clear of each other
    if (pillY !== null && Math.abs(y - pillY) < 26) y = pillY + (y <= pillY ? -26 : 26);
    const hit = placed.find(t => Math.abs(t.y - y) < gap);
    if (hit) { hit.also.push(d.z.rank); continue; }
    placed.push({ rank: d.z.rank, y, side: d.side, also: [], text: fmt(d.z) });
  }
  return placed;
}
